import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography,
  Switch, MenuItem, FormControlLabel, Checkbox, CircularProgress,
} from '@mui/material';
import { useToast } from './Toast';
import {
  ProviderOption, DaySchedule, QueueWritePayload,
  fetchQueueProviders, createQueue, updateQueue,
} from '../hooks/useQueues';
import { colors } from '../theme/theme';

const DAYS: Array<{ key: string; label: string }> = [
  { key: 'monday', label: 'Mon' }, { key: 'tuesday', label: 'Tue' }, { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' }, { key: 'friday', label: 'Fri' }, { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

export interface QueueFormInitial {
  id: string;
  name: string;
  business_id: string;
  booking_mode?: string;
  provider_user_ids: string[];
  schedule: DaySchedule[]; // normalised, lowercase day keys
}

interface Props {
  open: boolean;
  mode: 'add' | 'edit';
  onClose: () => void;
  onSaved: () => void;
  businesses?: Array<{ id: string; name: string }>; // add mode
  initial?: QueueFormInitial;                        // edit mode
}

interface DayState { enabled: boolean; start: string; end: string; max: number }

const defaultDays = (): Record<string, DayState> => {
  const out: Record<string, DayState> = {};
  DAYS.forEach((d) => {
    out[d.key] = { enabled: d.key !== 'saturday' && d.key !== 'sunday', start: '09:00', end: '17:00', max: 20 };
  });
  return out;
};

const fromInitial = (schedule: DaySchedule[]): Record<string, DayState> => {
  const out = defaultDays();
  schedule.forEach((s) => {
    const k = s.day.toLowerCase();
    if (out[k]) {
      out[k] = {
        enabled: !!s.is_enabled,
        start: s.start_time || '09:00',
        end: s.end_time || '17:00',
        max: s.max_tokens ?? 20,
      };
    }
  });
  return out;
};

const QueueFormDialog: React.FC<Props> = ({ open, mode, onClose, onSaved, businesses = [], initial }) => {
  const toast = useToast();
  const [businessId, setBusinessId] = useState('');
  const [name, setName] = useState('');
  const [bookingMode, setBookingMode] = useState('public');
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [days, setDays] = useState<Record<string, DayState>>(defaultDays());
  const [sameForAll, setSameForAll] = useState(true);
  const [updateSlots, setUpdateSlots] = useState(true);
  const [saving, setSaving] = useState(false);

  // (Re)initialise the form whenever it opens.
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setBusinessId(initial.business_id);
      setName(initial.name);
      setBookingMode(initial.booking_mode || 'public');
      setSelected(initial.provider_user_ids);
      setDays(fromInitial(initial.schedule));
    } else {
      setBusinessId('');
      setName('');
      setBookingMode('public');
      setSelected([]);
      setDays(defaultDays());
    }
    setSameForAll(true);
    setUpdateSlots(true);
  }, [open, mode, initial]);

  // Load the assignable providers for the chosen business.
  const loadProviders = useCallback(async (bid: string) => {
    if (!bid) { setProviders([]); return; }
    setLoadingProviders(true);
    try {
      setProviders(await fetchQueueProviders(bid));
    } catch {
      setProviders([]);
      toast('Failed to load providers for this business', 'error');
    } finally {
      setLoadingProviders(false);
    }
  }, [toast]);

  useEffect(() => { if (open && businessId) void loadProviders(businessId); }, [open, businessId, loadProviders]);

  const setDay = (key: string, patch: Partial<DayState>) =>
    setDays((d) => ({ ...d, [key]: { ...d[key], ...patch } }));

  const enabledCount = useMemo(() => Object.values(days).filter((d) => d.enabled).length, [days]);

  const buildSchedule = (): DaySchedule[] =>
    DAYS.map(({ key }) => {
      const d = days[key];
      return { day: key, is_enabled: d.enabled, start_time: d.start, end_time: d.end, max_tokens: d.max };
    });

  const submit = async () => {
    if (mode === 'add' && !businessId) return toast('Select a business', 'error');
    if (name.trim().length < 2) return toast('Queue name must be at least 2 characters', 'error');
    if (selected.length === 0) return toast('Select at least one provider', 'error');
    if (enabledCount === 0) return toast('Enable at least one day in the schedule', 'error');
    const bad = DAYS.find(({ key }) => days[key].enabled && days[key].start >= days[key].end);
    if (bad) return toast(`${bad.label}: start time must be before end time`, 'error');

    const payload: QueueWritePayload = {
      name: name.trim(),
      provider_ids: selected,
      schedule: buildSchedule(),
      booking_mode: bookingMode,
      same_for_all_days: sameForAll,
    };

    setSaving(true);
    try {
      if (mode === 'add') {
        await createQueue(businessId, payload);
        toast('Queue created', 'success');
      } else if (initial) {
        await updateQueue(initial.id, { ...payload, update_existing_slots: updateSlots });
        toast('Queue updated', 'success');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      toast(err?.response?.data?.message || 'Failed to save queue', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleProvider = (uid: string) =>
    setSelected((s) => (s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]));

  const fieldSx = { mb: 2 };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>{mode === 'add' ? 'Add Queue' : 'Edit Queue'}</DialogTitle>
      <DialogContent dividers>
        {mode === 'add' && (
          <TextField select fullWidth size="small" label="Business" value={businessId}
            onChange={(e) => { setBusinessId(e.target.value); setSelected([]); }} sx={fieldSx}>
            {businesses.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
          </TextField>
        )}

        <TextField fullWidth size="small" label="Queue name" value={name} onChange={(e) => setName(e.target.value)} sx={fieldSx} />

        <TextField select fullWidth size="small" label="Booking mode" value={bookingMode} onChange={(e) => setBookingMode(e.target.value)} sx={fieldSx}>
          <MenuItem value="public">Public (open self-booking)</MenuItem>
          <MenuItem value="controlled_public">Controlled public (start/stop per day)</MenuItem>
          <MenuItem value="private">Private (provider-only)</MenuItem>
        </TextField>

        <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Providers</Typography>
        <Box sx={{ border: `1px solid ${colors.border}`, borderRadius: '10px', p: 1, mb: 2, maxHeight: 160, overflowY: 'auto' }}>
          {loadingProviders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
          ) : !businessId ? (
            <Typography sx={{ color: 'text.secondary', fontSize: 13, p: 1 }}>Select a business first.</Typography>
          ) : providers.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', fontSize: 13, p: 1 }}>No assignable providers for this business.</Typography>
          ) : providers.map((p) => (
            <FormControlLabel key={p.user_id} sx={{ display: 'flex', m: 0 }}
              control={<Checkbox size="small" checked={selected.includes(p.user_id)} onChange={() => toggleProvider(p.user_id)} />}
              label={<Typography sx={{ fontSize: 13.5 }}>{p.name} <Typography component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>· {p.role}</Typography></Typography>}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Weekly schedule</Typography>
          <FormControlLabel control={<Switch size="small" checked={sameForAll} onChange={(e) => setSameForAll(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12.5 }}>Same hours for all days</Typography>} />
        </Box>

        {DAYS.map(({ key, label }) => {
          const d = days[key];
          const showTimes = !sameForAll || key === DAYS.find((x) => days[x.key].enabled)?.key;
          return (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <FormControlLabel sx={{ width: 92, m: 0 }}
                control={<Switch size="small" checked={d.enabled} onChange={(e) => setDay(key, { enabled: e.target.checked })} />}
                label={<Typography sx={{ fontSize: 13 }}>{label}</Typography>} />
              {d.enabled && showTimes ? (
                <>
                  <TextField type="time" size="small" value={d.start} onChange={(e) => setDay(key, { start: e.target.value })} sx={{ width: 120 }} />
                  <TextField type="time" size="small" value={d.end} onChange={(e) => setDay(key, { end: e.target.value })} sx={{ width: 120 }} />
                  <TextField type="number" size="small" label="Max" value={d.max} onChange={(e) => setDay(key, { max: Math.max(1, parseInt(e.target.value || '1', 10)) })} sx={{ width: 90 }} inputProps={{ min: 1, max: 1000 }} />
                </>
              ) : d.enabled ? (
                <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>follows the first enabled day</Typography>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: colors.textTertiary }}>closed</Typography>
              )}
            </Box>
          );
        })}

        {mode === 'edit' && (
          <FormControlLabel sx={{ mt: 1 }}
            control={<Checkbox size="small" checked={updateSlots} onChange={(e) => setUpdateSlots(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12.5 }}>Apply changes to today & future open slots</Typography>} />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button onClick={submit} disabled={saving} variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {mode === 'add' ? 'Create Queue' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QueueFormDialog;
